import React, { type CSSProperties, type ReactNode } from 'react'
import styled from '@emotion/styled'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { PillButton } from '@mobi/component-library/Base/Buttons/PillButton'
import {
  colors,
  font,
  layering,
  measurements,
  radius,
  shadow,
  spacing,
} from '@mobi/component-library/Foundations'
import { useRenderTimeoutControl } from '@mobi/component-library/Utils/hooks/useRenderTimeoutControl'
import { useBodyScrollLock } from '@mobi/utils/hooks/useBodyScrollLock'

const enum LocalConstants {
  AnimationTimingMs = 400,
}

type Props = {
  /** Whether the tray is open and should be rendered */
  isOpen: boolean
  /** The header title of the tray */
  headerTitle?: string | ReactNode
  headerTitleAlignment?: 'left' | 'center'
  /** should cover most of the screen, If not height will match contents of tray */
  shouldCoverFullscreen?: boolean
  /** The children to render */
  children?: ReactNode
  /**
   * How the close button and backdrop behave:
   * - allowed - Clicking the button or backdrop dismisses the tray (default)
   * - disallowed - The button is hidden. Clicking the backdrop does nothing
   * - disabled - The button is visible, but disabled. Clicking the backdrop does nothing
   */
  dismissStatus?: 'allowed' | 'disallowed' | 'disabled'
  /** Callback when the user has dismissed the modal */
  onDismiss?: VoidFunction
  /** Overrides for the tray content styling */
  trayStyle?: Partial<CSSProperties>
  /** Callback to return to previous page when tray has more than 1 'page' */
  onBackNavigate?: VoidFunction
  /** Always visible footer section, best suited to action buttons within Fragment */
  footerComponent?: ReactNode
}

export const Tray: React.FC<Props> = ({
  isOpen,
  headerTitle,
  headerTitleAlignment = 'left',
  onDismiss,
  dismissStatus = 'allowed',
  shouldCoverFullscreen = false,
  children,
  trayStyle,
  onBackNavigate,
  footerComponent,
}) => {
  const shouldRenderTray = useRenderTimeoutControl({
    shouldRender: isOpen,
    timeoutMs: LocalConstants.AnimationTimingMs as number,
  })
  const scrollContainerRef = useBodyScrollLock(shouldRenderTray)

  if (!shouldRenderTray) return null

  const wrapperClassNames: string[] = []
  if (!isOpen) wrapperClassNames.push('component-tray--unmounting')
  if (shouldCoverFullscreen) wrapperClassNames.push('component-tray--fullscreen')
  if (footerComponent) wrapperClassNames.push('component-tray--has-footer')

  const canBeDismissed = dismissStatus === 'allowed'
  const isCloseButtonVisible = dismissStatus !== 'disallowed'
  const shouldRenderHeader = onBackNavigate || headerTitle || isCloseButtonVisible

  return (
    <>
      <WrapperStyled
        role='dialog'
        aria-modal='true'
        data-testid='Overlay.Tray'
        className={wrapperClassNames.join(' ')}
      >
        <article style={trayStyle}>
          {shouldRenderHeader && (
            <header data-header-align={headerTitleAlignment}>
              {onBackNavigate ? (
                <PillButton
                  size='sm'
                  color='secondary_grey'
                  isIconOnlyButton
                  onClick={onBackNavigate}
                  data-testid='Overlay.Tray.Back.Nav'
                  aria-label='Navigate back'
                >
                  <Icon onClick={onBackNavigate} name='SolidChevronLeft' size='2rem' />
                </PillButton>
              ) : headerTitleAlignment === 'center' ? (
                <span className='tray__header-spacer' />
              ) : null}

              {headerTitle && <h2 data-testid='Overlay.Tray.Title'>{headerTitle}</h2>}

              {isCloseButtonVisible ? (
                <PillButton
                  size='sm'
                  color='secondary_grey'
                  isIconOnlyButton
                  onClick={onDismiss}
                  className='tray__close-btn'
                  data-testid='Overlay.Tray.Close'
                  aria-label='Close tray'
                  disabled={!canBeDismissed}
                >
                  <Icon name='SolidXClose' size='2rem' />
                </PillButton>
              ) : null}
            </header>
          )}

          <div ref={scrollContainerRef} data-testid='Overlay.Tray.Content'>
            {children}
          </div>

          {footerComponent && <footer>{footerComponent}</footer>}
        </article>
      </WrapperStyled>

      <div
        role={canBeDismissed ? 'button' : 'presentation'}
        aria-label='Tray backdrop'
        className='tray-component__dimmer'
        onClick={canBeDismissed ? onDismiss : undefined}
      />
    </>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  '--bottom-padding': `calc(var(--safe-area-inset-bottom, 0px) + ${spacing.lg})`,

  position: 'fixed',
  zIndex: layering.overlayHigh,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,

  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  overflow: 'hidden',
  pointerEvents: 'none',

  '> article': {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
    width: '100%',
    minWidth: measurements.mobi.minWidth,
    maxWidth: measurements.mobi.maxWidth,
    padding: `${spacing.md} ${spacing.md} 0`,
    margin: '0 auto',
    overflow: 'hidden',
    pointerEvents: 'all',
    touchAction: 'manipulation',
    background: colors.white,
    borderRadius: `${radius.lgx4} ${radius.lgx4} 0 0`,
    boxShadow: shadow.lg,

    animationName: 'trayIn',
    animationFillMode: 'forwards',
    animationDuration: LocalConstants.AnimationTimingMs + 'ms',
    animationTimingFunction: 'cubic-bezier(0.165, 0.84, 0.44, 1)',

    '> header': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: spacing.smx2,
      paddingBottom: spacing.md,

      '> h2': {
        flex: 1,
        padding: 0,
        margin: 0,
        background: 0,
        color: colors.black,
        fontFamily: font.family.primary,
        fontSize: font.size.xl2.fontSize,
        letterSpacing: font.size.xl2.letterSpacing,
        lineHeight: 1.25,
        fontWeight: font.weight.semibold,
        textShadow: 'none',
        textTransform: 'unset',
        textAlign: 'left',
        wordBreak: 'break-word',
      },

      '> *': { flexShrink: 0 },

      '.tray__close-btn': { marginLeft: 'auto' },
      '.tray__header-spacer': { display: 'none' },

      '&[data-header-align="center"]': {
        '> .tray__header-spacer': { display: 'block', width: '3.6rem', height: '3.6rem' },

        '> h2': { textAlign: 'center' },
      },
    },

    // Children container
    '> div': {
      flex: 1,
      overflowY: 'auto',
      scrollBehavior: 'smooth',
      overscrollBehavior: 'contain',
      WebkitOverflowScrolling: 'touch',
      WebkitTapHighlightColor: 'transparent',
      paddingBottom: 'var(--bottom-padding)',
    },

    '> footer': {
      display: 'flex',
      gap: spacing.smx1,
      paddingTop: spacing.md,
      paddingBottom: 'var(--bottom-padding)',
    },
  },

  '& + .tray-component__dimmer': {
    position: 'fixed',
    zIndex: layering.overlayHigh - 1,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    animation: `fadeIn ${LocalConstants.AnimationTimingMs}ms ease forwards`,
    backdropFilter: 'blur(1px)',
  },

  '&.component-tray--has-footer': {
    '> article > div': { paddingBottom: 0 },
  },

  '&.component-tray--fullscreen': {
    '> article': { minHeight: '90vh' },
  },

  '&.component-tray--unmounting': {
    '> article': {
      animationTimingFunction: 'cubic-bezier(0.550, 0.085, 0.680, 0.530)',
      animationName: 'trayOut',
    },

    '+ .tray-component__dimmer': {
      animation: `fadeOut ${LocalConstants.AnimationTimingMs}ms ease forwards`,
    },
  },

  '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
  '@keyframes fadeOut': { from: { opacity: 1 }, to: { opacity: 0 } },
  '@keyframes trayIn': {
    from: { transform: 'translateY(100%)' },
    to: { transform: 'translateY(0)' },
  },
  '@keyframes trayOut': {
    from: { transform: 'translateY(0)' },
    to: { transform: 'translateY(105%)' },
  },
})
